import {
  ref,
  reactive,
  watch,
  computed,
  onUnmounted,
} from 'vue'
import { useMyStore } from '/@/store'
import { postDrc } from '/@/api/drc'
import {
  UranusMqtt,
  MqttStatusEvent,
} from '/@/mqtt'
import { message } from 'ant-design-vue'
import {
  DRC_CREDENTIAL_REFRESH_LEAD_MS,
  drcCredentialRefreshDelayMs,
  drcErrorMessage,
} from '/@/utils/drc-lease'

export type DrcMqttLinkStatus = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'closed' | 'auth_failed'

export interface DrcMqttLink {
  status: DrcMqttLinkStatus
  clientId: string
  /** JWT expiry as epoch seconds, as issued by `drc/connect`. */
  expireTime: number | null
  lastError: string
}

/** Retry the credential request with a capped back-off while the panel stays open. */
const CREDENTIAL_RETRY_DELAYS_MS = [3000, 5000, 10000, 20000, 30000]
/** Do not re-request credentials more often than this after a broker close/error. */
const CREDENTIAL_REFRESH_MIN_INTERVAL_MS = 15000

/**
 * Owns the browser-side DRC MQTT connection.
 *
 * The backend issues a JWT that expires after `expire_sec` (3600 s by default)
 * and the broker rejects the next CONNECT once it has. The previous version of
 * this hook connected once and never refreshed, so a cockpit left open for an
 * hour lost heartbeats and stick control with no explanation. Credentials are
 * now refreshed for the same `client_id` before they expire (the backend only
 * lets the issuing account refresh its own id), and immediately after a
 * close/error that happens inside the expiry window. The client id is kept
 * stable because it is half of the DRC lease identity.
 */
export function useConnectMqtt () {
  const store = useMyStore()
  const dockOsdVisible = computed(() => {
    return store.state.osdVisible && store.state.osdVisible.visible && store.state.osdVisible.is_dock
  })
  const mqttState = ref<UranusMqtt | null>(null)
  const link = reactive<DrcMqttLink>({
    status: 'idle',
    clientId: '',
    expireTime: null,
    lastError: '',
  })

  let refreshTimer: ReturnType<typeof setTimeout> | undefined
  let retryTimer: ReturnType<typeof setTimeout> | undefined
  let retryAttempt = 0
  let connectInFlight = false
  let lastCredentialRequestAt = 0
  let disposed = false

  function clearTimer (timer: ReturnType<typeof setTimeout> | undefined) {
    if (timer !== undefined) {
      clearTimeout(timer)
    }
  }

  function scheduleCredentialRefresh () {
    clearTimer(refreshTimer)
    refreshTimer = undefined
    const delay = drcCredentialRefreshDelayMs(link.expireTime)
    if (delay === null) {
      return
    }
    refreshTimer = setTimeout(() => {
      refreshTimer = undefined
      connect()
    }, delay)
  }

  function scheduleRetry () {
    clearTimer(retryTimer)
    const delay = CREDENTIAL_RETRY_DELAYS_MS[Math.min(retryAttempt, CREDENTIAL_RETRY_DELAYS_MS.length - 1)]
    retryAttempt += 1
    retryTimer = setTimeout(() => {
      retryTimer = undefined
      connect()
    }, delay)
  }

  function credentialsNearExpiry (now = Date.now()) {
    if (!link.expireTime) {
      return false
    }
    const expireAtMs = link.expireTime > 1e12 ? link.expireTime : link.expireTime * 1000
    return expireAtMs - now <= DRC_CREDENTIAL_REFRESH_LEAD_MS
  }

  /** A close/error near `expire_time` means the broker rejected the JWT: fetch a new one now. */
  function refreshIfCredentialsExpired () {
    if (!credentialsNearExpiry() || connectInFlight) {
      return
    }
    if (Date.now() - lastCredentialRequestAt < CREDENTIAL_REFRESH_MIN_INTERVAL_MS) {
      return
    }
    connect()
  }

  function handleStatus (event: MqttStatusEvent) {
    switch (event.status) {
      case 'open':
        link.status = 'open'
        break
      case 'reconnecting':
        link.status = 'reconnecting'
        refreshIfCredentialsExpired()
        break
      case 'close':
        if (link.status !== 'auth_failed') {
          link.status = 'closed'
        }
        refreshIfCredentialsExpired()
        break
      case 'error':
        link.lastError = event.data?.message || 'MQTT 连接异常'
        refreshIfCredentialsExpired()
        break
      default:
        break
    }
  }

  async function connect () {
    if (disposed || !dockOsdVisible.value || connectInFlight) {
      return
    }
    connectInFlight = true
    clearTimer(retryTimer)
    retryTimer = undefined
    if (!mqttState.value) {
      link.status = 'connecting'
    }
    lastCredentialRequestAt = Date.now()
    try {
      const result = await postDrc(link.clientId ? { client_id: link.clientId } : {}, { skipErrorMessage: true })
      if (result?.code !== 0 || !result.data) {
        throw new Error(result?.message || 'DRC 控制链路认证失败')
      }
      if (disposed || !dockOsdVisible.value) {
        return
      }
      const { address, client_id, username, password, expire_time } = result.data
      const expireTime = Number(expire_time)
      link.expireTime = Number.isFinite(expireTime) && expireTime > 0 ? expireTime : null
      link.lastError = ''
      if (mqttState.value && link.clientId === client_id) {
        // Same identity: swap the JWT in place so subscriptions and handlers survive.
        mqttState.value.updateCredentials(username, password)
        mqttState.value.reconnect()
      } else {
        mqttState.value?.destroyed()
        const client = new UranusMqtt(address, {
          clientId: client_id,
          username,
          password,
        })
        client.on('onStatus', handleStatus)
        client.initMqtt()
        mqttState.value = client
        link.clientId = client_id
        store.commit('SET_MQTT_STATE', client)
        store.commit('SET_CLIENT_ID', client_id)
      }
      retryAttempt = 0
      scheduleCredentialRefresh()
    } catch (error) {
      const text = drcErrorMessage(error, 'DRC 控制链路认证失败，请检查后端与 MQTT 服务')
      link.lastError = text
      if (!mqttState.value) {
        link.status = 'auth_failed'
      }
      if (retryAttempt === 0) {
        message.error(`${text}，将自动重试`)
      }
      scheduleRetry()
    } finally {
      connectInFlight = false
    }
  }

  function teardown () {
    clearTimer(refreshTimer)
    clearTimer(retryTimer)
    refreshTimer = undefined
    retryTimer = undefined
    retryAttempt = 0
    if (mqttState.value) {
      mqttState.value.destroyed()
      mqttState.value = null
      store.commit('SET_MQTT_STATE', null)
      store.commit('SET_CLIENT_ID', '')
    }
    link.status = 'idle'
    link.clientId = ''
    link.expireTime = null
    link.lastError = ''
  }

  // The dock panel (or cockpit) being visible is what keeps the control link alive.
  watch(() => dockOsdVisible.value, (visible) => {
    if (visible) {
      if (mqttState.value) return
      connect()
      return
    }
    teardown()
  }, { immediate: true })

  onUnmounted(() => {
    disposed = true
    teardown()
  })

  return {
    mqttState,
    link,
    /** Re-request credentials now (used by the cockpit's "刷新控制链路" when the link is down). */
    reconnect: connect,
  }
}
